import { format, isValid, parse, parseISO } from 'date-fns';

function candidateBirthToKolayDate(birthDate) {
	if (birthDate === null || birthDate === undefined || birthDate === '') {
		return null;
	}
	if (typeof birthDate === 'string') {
		const s = birthDate.trim();
		const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
		if (isoPrefix) {
			const d = parseISO(isoPrefix[1]);
			if (isValid(d)) {
				return format(d, 'yyyy-MM-dd');
			}
		}
		const dot = s.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
		if (dot) {
			const d = parse(s, 'dd.MM.yyyy', new Date());
			if (isValid(d)) {
				return format(d, 'yyyy-MM-dd');
			}
		}
	}
	const d = new Date(birthDate);
	if (isValid(d)) {
		return format(d, 'yyyy-MM-dd');
	}
	return null;
}

const RESUME_EDU_TO_KOLAY = [
	{ re: /doctor|doktora|ph\.?\s*d/i, value: 'doctorate' },
	{ re: /master|yüksek\s*lisans|msc|mba|\bms\b/i, value: 'master' },
	{ re: /bachelor|lisans|üniversite|university|ba\s|bs\s|b\.?s\.?c/i, value: 'bachelor' },
	{ re: /associate|önlisans|ön\s*lisans|a\.?a\.?s/i, value: 'associate' },
	{ re: /high\s*school|lise|secondary\s*school/i, value: 'high_school' },
	{ re: /secondary|ortaokul|middle/i, value: 'secondary' },
	{ re: /primary|ilkokul|elementary/i, value: 'primary' },
];

function mapResumeEducationLevelToKolay(level) {
	if (!level || typeof level !== 'string') {
		return null;
	}
	const t = level.trim();
	if (!t) {
		return null;
	}
	const direct = [
		'primary',
		'secondary',
		'high_school',
		'associate',
		'bachelor',
		'master',
		'doctorate',
		'none',
	];
	if (direct.includes(t)) {
		return t;
	}
	for (let i = 0; i < RESUME_EDU_TO_KOLAY.length; i += 1) {
		if (RESUME_EDU_TO_KOLAY[i].re.test(t)) {
			return RESUME_EDU_TO_KOLAY[i].value;
		}
	}
	return null;
}

export function getKolayRelatedFields(candidate) {
	const kolayFields = {};
	if (candidate.email && candidate.email.length > 0) {
		kolayFields.email = candidate.email[0];
	}
	if (candidate.email && candidate.email.length > 1 && candidate.email[1]) {
		kolayFields.workEmail = candidate.email[1];
	}
	if (candidate.name) {
		const nameList = candidate.name.trim().split(/\s+/).filter(Boolean);
		if (nameList.length === 1) {
			kolayFields.firstName = nameList[0];
		}
		if (nameList.length > 1) {
			kolayFields.firstName = nameList.slice(0, nameList.length - 1).join(' ');
			kolayFields.lastName = nameList[nameList.length - 1];
		}
	}
	if (
		candidate.education &&
		candidate.education.length > 0 &&
		candidate.education[0].organization
	) {
		kolayFields.educationalInstitution = candidate.education[0].organization;
	} else if (candidate.last_school) {
		kolayFields.educationalInstitution = candidate.last_school;
	}
	const edu0 = candidate.education && candidate.education[0];
	if (edu0 && edu0.level) {
		const mapped = mapResumeEducationLevelToKolay(String(edu0.level));
		if (mapped) {
			kolayFields.educationLevel = mapped;
		}
	}
	if (candidate.phone && candidate.phone.length > 0) {
		kolayFields.mobilePhone = candidate.phone[0];
	}
	if (candidate.phone && candidate.phone.length > 1 && candidate.phone[1]) {
		kolayFields.workPhone = candidate.phone[1];
	}
	if (candidate.country_code && String(candidate.country_code).length >= 2) {
		kolayFields.nationality = String(candidate.country_code).toUpperCase().slice(0, 2);
	}
	const bd = candidateBirthToKolayDate(candidate.birth_date);
	if (bd) {
		kolayFields.birthday = bd;
	}
	const natForId = kolayFields.nationality || 'TR';
	if (natForId === 'TR' && candidate.identification_number) {
		const digits = String(candidate.identification_number).replace(/\D/g, '');
		if (digits.length === 11) {
			kolayFields.idNumber = digits;
		}
	}
	return kolayFields;
}

export function buildKolayPostPayload({ kolay, manager, units }) {
	return {
		files: [],
		person: {
			...kolay,
			unitList: [
				{
					startDate: kolay.employmentStartDate
						? kolay.employmentStartDate
						: format(new Date(), 'yyyy-MM-dd'),
					endDate: kolay.contractEndDate ? kolay.contractEndDate : null,
					managerId: manager && manager.id ? manager.id : null,
					active: true,
					default: true,
					items: units.map(unit => ({
						unitName: unit.name,
						unitItemName: unit.value,
					})),
				},
			],
		},
	};
}

export function isEligibleForBulkKolaySync(candidate) {
	if (!candidate) {
		return false;
	}
	if (candidate.is_synced_with_kolay_ik === true) {
		return false;
	}
	const apps = candidate.applications || [];
	return apps.some(
		a => a && a.is_archived && a.archive_status === 'archive-hired',
	);
}

export function parseKolayBulkResponse(response, itemsLength, candidates) {
	const raw = response && response.data;
	const d =
		raw && raw.data !== undefined && typeof raw.data === 'object'
			? raw.data
			: raw;
	const failures = Array.isArray(d.failures)
		? d.failures
		: Array.isArray(d.failed)
			? d.failed
			: [];
	let ok = d.success_count ?? d.successCount;
	if (typeof ok !== 'number') {
		ok = Math.max(0, itemsLength - failures.length);
	}
	const byId = new Map(candidates.map(c => [c.id, c]));
	const failureDetails = failures.map(f => {
		const id = f.candidate_id ?? f.candidateId ?? f.id;
		const c = byId.get(id);
		const name = c ? c.name : String(id);
		const message =
			(typeof f.message === 'string' && f.message) ||
			(typeof f.error === 'string' && f.error) ||
			'';
		return { name, message };
	});
	return { ok, failureDetails };
}
